import React, { useState } from 'react';
import { FiX, FiSlash } from 'react-icons/fi';

import { useBlocklistCategories } from '@/features/onboarding/hooks/useBlocklist';

export interface BlocklistData {
  blockField: string;
  reasonCategory: string;
  reasonDetails: string;
}

export interface BlocklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: BlocklistData) => void;
  clientName: string;
}

export const BlocklistModal: React.FC<BlocklistModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clientName,
}) => {
  const [blockField, setBlockField] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');

  const { data: categories, isLoading: categoriesLoading } = useBlocklistCategories();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm({
      blockField,
      reasonCategory,
      reasonDetails,
    });
    // Reset form
    setBlockField('');
    setReasonCategory('');
    setReasonDetails('');
  };

  const handleCancel = () => {
    // Reset form
    setBlockField('');
    setReasonCategory('');
    setReasonDetails('');
    onClose();
  };

  const fieldOptions = ['CPF/CNPJ', 'Email', 'Telefone', 'Endereço', 'Sócio'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-neutral-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <FiSlash className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Adicionar ao Blacklist</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Forneça os detalhes para o envio ao Blacklist da conta da empresa {clientName}.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Campos para bloqueio */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Campos para bloqueio <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={blockField}
                onChange={(e) => {
                  setBlockField(e.target.value);
                }}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm text-neutral-900"
              >
                <option value="">Selecionar</option>
                {fieldOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Categoria do motivo */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Categoria do motivo <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={reasonCategory}
                onChange={(e) => {
                  setReasonCategory(e.target.value);
                }}
                disabled={categoriesLoading}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm text-neutral-900 disabled:bg-neutral-100"
              >
                <option value="">{categoriesLoading ? 'Carregando...' : 'Selecionar'}</option>
                {categories?.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Detalhes do motivo */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Detalhes do motivo <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reasonDetails}
              onChange={(e) => {
                setReasonDetails(e.target.value);
              }}
              placeholder="Inserir"
              rows={6}
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2.5 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!blockField || !reasonCategory || !reasonDetails}
            className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:bg-neutral-300 disabled:cursor-not-allowed"
          >
            Confirmar Blacklist
          </button>
        </div>
      </div>
    </div>
  );
};
